import { LocalStorageState } from "@mixitone/components";
import { Club, Night } from "@mixitone/models";
import { batch } from "@mixitone/mvc";
import { withStateToggle } from "@mixitone/util";
import { AppController } from "controllers/AppController";
import MixitoneController from "controllers/MixitoneController";
import RouteController from "controllers/RouteController";

interface State {
  night: Night;
  loading: boolean;
  club: Club;
  updating: boolean;
  courts: number;
  player_fee?: number;
  game_set_time?: number;
  warm_up_time?: number;
  defaultTab: string;
  showCancelAlert: boolean;
  cancelling: boolean;
}

interface Props {
  token: string;
}

export class SettingsController extends MixitoneController<State, Props> {
  static override initialState: Partial<State> = {
    loading: true,
    updating: false,
    courts: 5,
    player_fee: undefined,
    game_set_time: 15,
    warm_up_time: 10,
    defaultTab: "Session",
    showCancelAlert: false,
    cancelling: false,
  };

  defaultTabState?: LocalStorageState<string>;

  override async initialize({ token }: Props) {
    const tab = this.get(RouteController).state.location.state?.tab;

    this.defaultTabState = new LocalStorageState<string>(`SettingsTab-${token}`, undefined, {
      session: true,
    });

    if (tab) {
      this.defaultTabState.set(tab);
    }

    this.state.defaultTab = this.defaultTabState.get();
    this.state.night = await Night.query().find(token);
    this.state.club = await Club.query().find(this.state.night.club_id!);
    this.state.courts = this.night.courts || 5;
    this.state.game_set_time = this.night.game_set_time;
    this.state.warm_up_time = this.night.warm_up_time;
    this.state.player_fee = this.night.player_fee;

    // Basics loaded
    this.state.loading = false;
  }

  get night() {
    return this.state.night!;
  }

  async actionSubmit() {
    this.state.updating = true;

    await batch(async () => {
      await this.night.save();
      await this.night.ensureSets();
      await this.state.club!.groups.save();
      await this.state.club!.fill_rules.reload();
    });

    this.state.updating = false;
    this.actionCloseModal();
  }

  async actionSetDefaultTab(tab: string) {
    this.defaultTabState?.set(tab);
  }

  get canCancel() {
    return this.night.game_sets.every((gameSet) => gameSet.duration < 1);
  }

  actionCancel() {
    if (!this.canCancel) return;

    this.state.showCancelAlert = true;
  }

  actionDismissCancelAlert() {
    this.state.showCancelAlert = false;
  }

  @withStateToggle("cancelling")
  async actionConfirmCancelSession() {
    await this.night.destroy();
    this.state.showCancelAlert = false;
    await this.get(AppController).actionReturnOrHome();
  }
}
